import type { ApiProjectRow, ProjectDocument } from '../types/project';
import { apiFetch } from './apiClient';

// ── Base URL ──────────────────────────────────────────────────────────────────
// CRA proxy forwards /api/* to backend:4000 in Docker; in local dev the
// package.json proxy field handles it. We always use a relative path.

const BASE = '/api/projects';

// ── Response helpers ──────────────────────────────────────────────────────────

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = await res.json() as { error?: string; message?: string; errors?: Array<{ path: string; message: string }> };
      if (body.message || body.error) message = body.message ?? body.error ?? message;
      if (body.errors?.length) message += ` ${body.errors.slice(0, 3).map((issue) => `${issue.path}: ${issue.message}`).join('; ')}`;
    } catch {
      // ignore parse failure — use status text
      message = res.statusText || message;
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

// ── API functions ─────────────────────────────────────────────────────────────

/** List all projects (summary rows — project_json is included but may be large). */
export async function listProjects(): Promise<ApiProjectRow[]> {
  const res = await apiFetch(BASE);
  return handleResponse<ApiProjectRow[]>(res);
}

/** Fetch a single project by numeric ID. */
export async function getProject(id: number): Promise<ApiProjectRow> {
  const res = await apiFetch(`${BASE}/${id}`);
  return handleResponse<ApiProjectRow>(res);
}

/**
 * Create a new project.
 * Sends the canonical project JSON as the `project_json` field.
 * The backend stores `name` and `description` as top-level columns for
 * listing/search, and the full JSON as `project_json`.
 */
export async function createProject(
  name: string,
  description: string,
  doc: ProjectDocument,
): Promise<ApiProjectRow> {
  const res = await apiFetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      description,
      project_json: JSON.stringify(doc),
    }),
  });
  return handleResponse<ApiProjectRow>(res);
}

/**
 * Save (update) an existing project.
 * Always sends the full canonical project JSON so the backend stays in sync.
 */
export async function saveProject(
  id: number,
  name: string,
  description: string,
  doc: ProjectDocument,
): Promise<ApiProjectRow> {
  const res = await apiFetch(`${BASE}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      description,
      project_json: JSON.stringify(doc),
    }),
  });
  return handleResponse<ApiProjectRow>(res);
}

/** Create and immediately persist a blank builder project. */
export async function createSavedProject(name: string, description: string): Promise<ApiProjectRow> {
  return handleResponse<ApiProjectRow>(await apiFetch(BASE, {
    method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({name, description}),
  }));
}

/** Update metadata without sending a stale copy of the project document. */
export async function updateProjectDetails(id: number, name: string, description: string, expectedUpdatedAt: string): Promise<ApiProjectRow> {
  return handleResponse<ApiProjectRow>(await apiFetch(BASE + '/' + id, {
    method: 'PATCH', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({name, description, expectedUpdatedAt}),
  }));
}

export async function deleteSavedProject(id: number, updatedAt: string): Promise<void> {
  const response = await apiFetch(BASE + '/' + id, {method:'DELETE', headers:{'X-Project-Updated-At':updatedAt}});
  if (!response.ok) await handleResponse<never>(response);
}
